import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowRight,
  Home,
  Brain,
  BookOpen,
  Target,
  TrendingUp,
  TrendingDown
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const AREA_ICONS = {
  pensamiento_matematico: Brain,
  comprension_lectora: BookOpen,
  redaccion_indirecta: Target
};

const LEVEL_CONFIG = {
  bajo: {
    icon: XCircle,
    color: "text-red-600",
    bgColor: "bg-red-100",
    borderColor: "border-red-200",
    label: "Nivel Bajo",
    emoji: "🔴"
  },
  intermedio: {
    icon: AlertTriangle,
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
    borderColor: "border-yellow-200",
    label: "Nivel Intermedio",
    emoji: "🟡"
  },
  alto: {
    icon: CheckCircle,
    color: "text-green-600",
    bgColor: "bg-green-100",
    borderColor: "border-green-200",
    label: "Nivel Alto",
    emoji: "🟢"
  }
};

export default function ExamResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.result;

  if (!result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md" data-testid="no-results-card">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No hay resultados disponibles
            </h2>
            <p className="text-slate-600 mb-6">
              Parece que no has completado el examen diagnóstico.
            </p>
            <Button
              onClick={() => navigate("/examen-diagnostico")}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
              data-testid="go-to-exam-btn"
            >
              Ir al Examen Diagnóstico
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const levelConfig = LEVEL_CONFIG[result.level] || LEVEL_CONFIG.bajo;
  const LevelIcon = levelConfig.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <a href="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
            <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
          </a>
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-slate-600"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Inicio
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Main Result Card */}
        <Card className="mb-8 border-slate-200 shadow-lg overflow-hidden" data-testid="main-result-card">
          <div className={`${levelConfig.bgColor} px-6 py-4 border-b ${levelConfig.borderColor}`}>
            <div className="flex items-center justify-center gap-3">
              <LevelIcon className={`w-6 h-6 ${levelConfig.color}`} />
              <h2 className={`text-xl font-semibold ${levelConfig.color}`}>
                {levelConfig.emoji} {levelConfig.label}
              </h2>
            </div>
          </div>

          <CardContent className="p-6 md:p-8">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Resultado del Diagnóstico
              </h1>
              <p className="text-slate-600">
                Este es tu desempeño en el examen diagnóstico EXANI II
              </p>
            </div>

            {/* Score Overview */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-4 text-center" data-testid="score-correct">
                <p className="text-3xl font-bold text-purple-600">{result.total_score}</p>
                <p className="text-sm text-slate-500">Aciertos</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center" data-testid="score-total">
                <p className="text-3xl font-bold text-slate-700">{result.total_questions}</p>
                <p className="text-sm text-slate-500">Preguntas</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center" data-testid="score-percentage">
                <p className="text-3xl font-bold text-teal-600">{result.percentage}%</p>
                <p className="text-sm text-slate-500">Porcentaje</p>
              </div>
            </div>

            {/* Circular Progress */}
            <div className="flex justify-center mb-8">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#e2e8f0"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke={result.percentage >= 70 ? "#10b981" : result.percentage >= 50 ? "#f59e0b" : "#ef4444"}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${result.percentage * 4.4} 440`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-4xl font-bold text-slate-900">{result.percentage}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Area Results */}
        <Card className="mb-8 border-slate-200 shadow-sm" data-testid="area-results-card">
          <CardContent className="p-6 md:p-8">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
              Resultados por Área
            </h3>

            <div className="space-y-6">
              {result.areas.map((area, index) => {
                const Icon = AREA_ICONS[area.area] || Brain;
                const isStrong = area.percentage >= 60;

                return (
                  <div key={area.area} data-testid={`area-result-${index}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isStrong ? "bg-green-100" : "bg-red-100"
                        }`}>
                          <Icon className={`w-5 h-5 ${isStrong ? "text-green-600" : "text-red-600"}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{area.area_name}</p>
                          <p className="text-sm text-slate-500">
                            {area.correct} de {area.total} correctas
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-lg font-semibold ${
                          isStrong ? "text-green-600" : "text-red-600"
                        }`}>
                          {area.percentage}%
                        </span>
                      </div>
                    </div>
                    <Progress
                      value={area.percentage}
                      className={`h-3 ${isStrong ? "[&>div]:bg-green-500" : "[&>div]:bg-red-500"}`}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Strengths and Weaknesses */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strong Areas */}
          <Card className="border-green-100 bg-green-50/50" data-testid="strong-areas-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Áreas Fuertes
                </h3>
              </div>
              {result.strong_areas.length > 0 ? (
                <ul className="space-y-2">
                  {result.strong_areas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 text-sm">
                  Continúa practicando para fortalecer tus áreas
                </p>
              )}
            </CardContent>
          </Card>

          {/* Weak Areas */}
          <Card className="border-red-100 bg-red-50/50" data-testid="weak-areas-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Áreas a Mejorar
                </h3>
              </div>
              {result.weak_areas.length > 0 ? (
                <ul className="space-y-2">
                  {result.weak_areas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 text-sm">
                  ¡Excelente! No tienes áreas débiles
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recommendation */}
        <Card className="mb-8 border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="recommendation-card">
          <CardContent className="p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-purple-600" />
            </div>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
              Recomendación Sabeneval
            </h3>

            <p className="text-slate-600 mb-6 max-w-lg mx-auto">
              Te recomendamos el <strong>Curso Completo Sabeneval</strong> para mejorar tu puntaje 
              en el EXANI II. Nuestro programa cubre todas las áreas evaluadas y te brinda las 
              estrategias necesarias para obtener un mejor resultado.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/curso")}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-8"
                data-testid="view-course-btn"
              >
                Ver Curso Completo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate("/examen-diagnostico")}
                variant="outline"
                className="rounded-full h-12 px-8 border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="retry-exam-btn"
              >
                Repetir Diagnóstico
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
